import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card } from 'antd';

import {
  DashboardSummary,
  DashboardTicket,
} from '@totem/components/ticketing/ticketDashboard/types';

type Props = {
  tickets: DashboardTicket[];
  summary: DashboardSummary;
};

const AGE_BUCKET_COLORS = [
  '#22c55e',
  '#3b82f6',
  '#f59e0b',
  '#f97316',
  '#ef4444',
];
const AGE_BUCKETS = ['<24h', '1-7d', '8-30d', '31-60d', '60d+'];

const ActivityTab = ({ tickets, summary }: Props) => {
  const labels = summary.daily.map((day) => day.d.slice(5));
  const created = summary.daily.map((day) => day.c);
  const closed = summary.daily.map((day) => day.x);
  const net = summary.daily.map((_day, idx) => created[idx] - closed[idx]);

  const ageCounts = [
    tickets.filter((tkt) => tkt.ageH < 24).length,
    tickets.filter((tkt) => tkt.ageH >= 24 && tkt.ageH < 168).length,
    tickets.filter((tkt) => tkt.ageH >= 168 && tkt.ageH < 720).length,
    tickets.filter((tkt) => tkt.ageH >= 720 && tkt.ageH < 1440).length,
    tickets.filter((tkt) => tkt.ageH >= 1440).length,
  ];

  const flowOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: { bar: { borderRadius: 2, columnWidth: '70%' } },
    xaxis: { categories: labels, tickAmount: 10 },
    yaxis: { min: 0, labels: { formatter: (val) => String(Math.round(val)) } },
    colors: ['rgba(232,84,10,0.75)', 'rgba(34,197,94,0.75)'],
    legend: { position: 'top' },
    dataLabels: { enabled: false },
    tooltip: { shared: true, intersect: false },
  };

  const ageOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: {
      bar: { borderRadius: 2, columnWidth: '60%', distributed: true },
    },
    xaxis: { categories: AGE_BUCKETS },
    yaxis: { min: 0, labels: { formatter: (val) => String(Math.round(val)) } },
    colors: AGE_BUCKET_COLORS,
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  const netOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 2,
        columnWidth: '70%',
        colors: {
          ranges: [
            { from: -999, to: -1, color: 'rgba(34,197,94,0.8)' },
            { from: 0, to: 0, color: 'rgba(148,163,184,0.6)' },
            { from: 1, to: 999, color: 'rgba(239,68,68,0.8)' },
          ],
        },
      },
    },
    xaxis: { categories: labels, tickAmount: 10 },
    yaxis: { labels: { formatter: (val) => String(Math.round(val)) } },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card
          title={
            <span>
              Recent activity — last 30 days{' '}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: 'var(--ant-color-text-secondary)',
                }}
              >
                created vs closed
              </span>
            </span>
          }
          size="small"
        >
          <ReactApexChart
            type="bar"
            height={280}
            series={[
              { name: 'Created', data: created },
              { name: 'Closed', data: closed },
            ]}
            options={flowOptions}
          />
        </Card>

        <Card
          title={
            <span>
              Open ticket age{' '}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: 'var(--ant-color-text-secondary)',
                }}
              >
                how long each open ticket has been open
              </span>
            </span>
          }
          size="small"
        >
          <ReactApexChart
            type="bar"
            height={280}
            series={[{ name: 'Open', data: ageCounts }]}
            options={ageOptions}
          />
        </Card>
      </div>

      <Card
        title={
          <span>
            30-day net change{' '}
            <span
              style={{
                fontSize: 11,
                fontWeight: 400,
                color: 'var(--ant-color-text-secondary)',
              }}
            >
              positive = backlog growing
            </span>
          </span>
        }
        size="small"
      >
        <ReactApexChart
          type="bar"
          height={220}
          series={[{ name: 'Net (created − closed)', data: net }]}
          options={netOptions}
        />
      </Card>
    </div>
  );
};

export default ActivityTab;
